/**
 * Reinos de criatura (Kingdom) — categoría taxonómica de los NPCs.
 *
 * El id interno (`KingdomId`) está en inglés; el display en español ("Reino").
 * Se usa para colgar lógica de combate por tipo de criatura, empezando por el
 * modifier `damageVsKingdom` (+% daño contra un Reino). Es un sistema DINÁMICO:
 * agregar un Reino nuevo a `KingdomId` + `KINGDOMS` hace que su afijo de daño se
 * genere solo (ver shared/data/affixes.ts) — no hay que tocar nada más.
 *
 * Ver docs/CREATURE_KINGDOMS.md.
 */

export type KingdomId =
  | 'humanoid'
  | 'animal'
  | 'beast'
  | 'undead'
  | 'construct'
  | 'demon'
  | 'elemental'
  | 'dragon';

export interface KingdomDef {
  id: KingdomId;
  /** Nombre singular en español (ej. tooltip de un NPC). */
  labelEs: string;
  /** Nombre plural en español (ej. afijo "Daño vs Animales"). */
  labelEsPlural: string;
}

/**
 * Catálogo de Reinos. El orden es el de display. Los últimos tres
 * (demon/elemental/dragon) están RESERVADOS: existen en el enum (y por eso ya
 * generan su afijo de daño), pero todavía no hay NPCs asignados a ellos.
 */
export const KINGDOMS: readonly KingdomDef[] = [
  { id: 'humanoid', labelEs: 'Humanoide', labelEsPlural: 'Humanoides' },
  { id: 'animal', labelEs: 'Animal', labelEsPlural: 'Animales' },
  { id: 'beast', labelEs: 'Bestia', labelEsPlural: 'Bestias' },
  { id: 'undead', labelEs: 'No-muerto', labelEsPlural: 'No-muertos' },
  { id: 'construct', labelEs: 'Constructo', labelEsPlural: 'Constructos' },
  { id: 'demon', labelEs: 'Demonio', labelEsPlural: 'Demonios' },
  { id: 'elemental', labelEs: 'Elemental', labelEsPlural: 'Elementales' },
  { id: 'dragon', labelEs: 'Dragón', labelEsPlural: 'Dragones' },
] as const;

/** Reino por defecto de un NPC que no declara `kingdom` en su template. */
export const DEFAULT_KINGDOM: KingdomId = 'humanoid';

const KINGDOM_BY_ID: ReadonlyMap<KingdomId, KingdomDef> = new Map(
  KINGDOMS.map((k) => [k.id, k])
);

/** Def de un Reino por id. */
export function getKingdom(id: KingdomId): KingdomDef | undefined {
  return KINGDOM_BY_ID.get(id);
}

/** Label singular en español de un Reino (fallback: el id). */
export function getKingdomLabel(id: KingdomId): string {
  return KINGDOM_BY_ID.get(id)?.labelEs ?? id;
}
